import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Container,
  Row,
  Col,
  Form,
  Button,
  Offcanvas,
  Accordion,
  Badge,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../../api";
import { getAllEras, getEraMedicalAidNames } from "../../afinanceapi";
import { useNavigate, useLocation } from "react-router-dom";
import "./CreateInvoice.css";
import "./acc-list.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ERAList() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();

  const [eras, setERAs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [practiceList, setPracticeList] = useState([]);
  const [medicalAidOptions, setMedicalAidOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "header.ERADate",
    direction: "desc",
  });

  // Backend-driven filters
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedMedicalAids, setSelectedMedicalAids] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Temporary (offcanvas) filters before Apply
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpMedicalAids, setTmpMedicalAids] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const limit = 20;

  const currency = useMemo(
    () => new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" }),
    [],
  );

  // ─── Default date range: 1 month back, restorable from URL ────────────────
  useEffect(() => {
    const p = query.get("practices");
    const ma = query.get("medicalAids");
    const st = query.get("start");
    const en = query.get("end");

    if (st || en || p || ma) {
      if (p) {
        const arr = p.split(",");
        setSelectedPractices(arr);
        setTmpPractices(arr);
      }
      if (ma) {
        const arr = ma.split(",");
        setSelectedMedicalAids(arr);
        setTmpMedicalAids(arr);
      }
      if (st) {
        setStartDate(st);
        setTmpStartDate(st);
      }
      if (en) {
        setEndDate(en);
        setTmpEndDate(en);
      }
    } else {
      const today = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      const iso = (d) => d.toISOString().slice(0, 10);
      setStartDate(iso(start));
      setEndDate(iso(today));
      setTmpStartDate(iso(start));
      setTmpEndDate(iso(today));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Load practice list + medical aid filter options once ─────────────────
  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return;
        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);

        const practiceNumbers = (practices || []).map((p) => p.practiceNumber);
        if (practiceNumbers.length > 0) {
          const names = await getEraMedicalAidNames(practiceNumbers);
          setMedicalAidOptions(names);
        }
      } catch (err) {
        console.error("Error loading practice list:", err);
      }
    })();
  }, []);

  // ─── Backend-driven fetch on every filter / sort / page change ────────────
  const fetchERAs = useCallback(async () => {
    if (!startDate || !endDate || practiceList.length === 0) return;
    try {
      setLoading(true);

      const practiceNumbers =
        selectedPractices.length > 0
          ? selectedPractices
          : practiceList.map((p) => p.practiceNumber);

      const result = await getAllEras({
        practiceIds: practiceNumbers,
        startDate,
        endDate,
        medicalAidNames: selectedMedicalAids,
        sortKey: sortConfig.key,
        sortDir: sortConfig.direction,
        page,
        limit,
      });

      setERAs(result?.data || []);
      setTotalCount(result?.total || 0);
      setExpandedRows(new Set());
    } catch (err) {
      console.error("Error fetching ERAs:", err);
      setERAs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    practiceList,
    selectedPractices,
    selectedMedicalAids,
    startDate,
    endDate,
    sortConfig,
    page,
  ]);

  useEffect(() => {
    fetchERAs();
  }, [fetchERAs]);

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  // ─── Filter offcanvas ─────────────────────────────────────────────────────
  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpMedicalAids(selectedMedicalAids);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedMedicalAids(tmpMedicalAids);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setShowFilters(false);
    setPage(1);

    const params = new URLSearchParams(location.search);
    params.delete("practices");
    params.delete("medicalAids");
    params.delete("start");
    params.delete("end");
    if (tmpPractices.length) params.set("practices", tmpPractices.join(","));
    if (tmpMedicalAids.length) params.set("medicalAids", tmpMedicalAids.join(","));
    if (tmpStartDate) params.set("start", tmpStartDate);
    if (tmpEndDate) params.set("end", tmpEndDate);
    navigate({ search: params.toString() }, { replace: true });
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedMedicalAids([]);
    const today = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    setStartDate(iso(start));
    setEndDate(iso(today));
    setTmpPractices([]);
    setTmpMedicalAids([]);
    setTmpStartDate(iso(start));
    setTmpEndDate(iso(today));
    setPage(1);
    const params = new URLSearchParams(location.search);
    params.delete("practices");
    params.delete("medicalAids");
    params.delete("start");
    params.delete("end");
    navigate({ search: params.toString() }, { replace: true });
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedMedicalAids.length ? 1 : 0) +
    (startDate && endDate ? 1 : 0);

  const handleHeaderClick = (era) => {
    navigate(`/billing/era/${era._id}`, {
      state: {
        breadcrumbTrail: [
          { label: "ERA List", path: `${location.pathname}${location.search}` },
        ],
      },
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Container fluid className="pt-2 acc-list">
      {/* DESKTOP TOP-BAR (>= 768px) — Filter / Clear / Pagination */}
      <div className="d-none d-md-block">
        <Row className="mb-3 align-items-center justify-content-end">
          <Col md="auto" className="d-flex gap-2 flex-wrap justify-content-end">
            <Button variant="primary" onClick={openFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2" />
              Filter{" "}
              {activeCount > 0 && (
                <Badge className="ms-2" bg="light" text="dark">
                  {activeCount}
                </Badge>
              )}
            </Button>
            <Button
              variant="outline-danger"
              onClick={clearFilters}
              style={{ width: "120px" }}
              disabled={
                selectedPractices.length === 0 &&
                selectedMedicalAids.length === 0 &&
                !startDate &&
                !endDate
              }
            >
              <i className="fa-solid fa-trash me-2" />
              Filters
            </Button>

            <div className="d-flex align-items-center gap-2 ms-2">
              <Button
                variant="primary"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <i className="fa-solid fa-angle-left" />
              </Button>
              <div className="text-nowrap small">
                Page {page} of {totalPages}
              </div>
              <Button
                variant="primary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <i className="fa-solid fa-angle-right" />
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      {/* MOBILE TOP-BAR (< 768px) — Filter + Pagination */}
      <div className="d-md-none mb-2">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button
            variant="primary"
            onClick={openFilters}
            className="flex-grow-1"
            style={{ minWidth: 110 }}
          >
            <i className="fa-solid fa-filter me-2" />
            Filter{" "}
            {activeCount > 0 && (
              <Badge className="ms-2" bg="light" text="dark">
                {activeCount}
              </Badge>
            )}
          </Button>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <span className="text-nowrap small">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="primary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p>Loading ERA logs...</p>
        </div>
      ) : eras.length === 0 ? (
        <p>No ERAs found. The default filter is for the last month.</p>
      ) : (
        <>
        <div className="d-none d-md-block">
        <Table bordered hover responsive className="fixed-table text-center align-middle">
          <thead>
            <tr>
              <th style={{ width: "3%" }}></th>
              <th style={{ width: "2%" }}>#</th>
              <th
                onClick={() => handleSort("header.ERADate")}
                style={{ cursor: "pointer", width: "8%" }}
                className="text-primary fw-semibold"
              >
                ERA Date {renderSortIcon("header.ERADate")}
              </th>
              <th
                onClick={() => handleSort("header.MedKreditERAnbr")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Switch Ref {renderSortIcon("header.MedKreditERAnbr")}
              </th>
              <th
                onClick={() => handleSort("header.PracticeNumber")}
                style={{ cursor: "pointer", width: "7%" }}
                className="text-primary fw-semibold"
              >
                Practice {renderSortIcon("header.PracticeNumber")}
              </th>
              <th
                onClick={() => handleSort("header.MedicalAidName")}
                style={{ cursor: "pointer", width: "16%" }}
                className="text-primary fw-semibold"
              >
                Medical Aid {renderSortIcon("header.MedicalAidName")}
              </th>
              <th
                onClick={() => handleSort("header.TotalPaidProvider")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Paid to Prov {renderSortIcon("header.TotalPaidProvider")}
              </th>
              <th
                onClick={() => handleSort("header.TotalPaidMember")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Paid to Mem {renderSortIcon("header.TotalPaidMember")}
              </th>
              <th
                onClick={() => handleSort("header.TotalNotPaid")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Not Paid {renderSortIcon("header.TotalNotPaid")}
              </th>
              <th
                onClick={() => handleSort("header.PaymentPaidDate")}
                style={{ cursor: "pointer", width: "8%" }}
                className="text-primary fw-semibold"
              >
                Payment Date {renderSortIcon("header.PaymentPaidDate")}
              </th>
              <th
                onClick={() => handleSort("header.PaymentReference")}
                style={{ cursor: "pointer", width: "8%" }}
                className="text-primary fw-semibold"
              >
                Payment Ref {renderSortIcon("header.PaymentReference")}
              </th>
            </tr>
          </thead>
          <tbody>
            {eras.map((era, idx) => {
              const h = era.header || {};
              const isExpanded = expandedRows.has(era._id);
              const summary = era.accountSummary || { allocated: [], unallocated: [] };
              const allocCount = (summary.allocated || []).reduce(
                (s, a) => s + (a.claimCount || 0),
                0,
              );
              const unallocCount = (summary.unallocated || []).reduce(
                (s, a) => s + (a.claimCount || 0),
                0,
              );

              return (
                <React.Fragment key={era._id}>
                  <tr
                    style={{
                      cursor: "pointer",
                      "--bs-table-bg": isExpanded ? "#CFE3FF4F" : "",
                      "--bs-table-color": isExpanded ? "#0f3490" : "",
                    }}
                    onClick={() => toggleRow(era._id)}
                  >
                    <td className="text-center align-middle">
                      <i
                        className={`fa-solid fa-chevron-${isExpanded ? "down" : "right"} text-primary`}
                      />
                    </td>
                    <td>{(page - 1) * limit + idx + 1}</td>
                    <td>{h.ERADate}</td>
                    <td
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleHeaderClick(era);
                      }}
                    >
                      {h.MedKreditERAnbr}
                    </td>
                    <td>{h.PracticeNumber}</td>
                    <td>{h.MedicalAidName}</td>
                    <td className="text-end acc-list-num">
                      {currency.format(h.TotalPaidProvider || 0)}
                    </td>
                    <td className="text-end acc-list-num">
                      {currency.format(h.TotalPaidMember || 0)}
                    </td>
                    <td className="text-end acc-list-num">
                      {currency.format(h.TotalNotPaid || 0)}
                    </td>
                    <td>{h.PaymentPaidDate}</td>
                    <td>{h.PaymentReference}</td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={11} className="p-0">
                        <ERADetailRow
                          summary={summary}
                          allocCount={allocCount}
                          unallocCount={unallocCount}
                          currency={currency}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
        </div>

        {/* MOBILE CARD BRANCH (Phase 38) — one card per ERA batch header.
            Top line: MedKreditERAnbr + TotalPaidProvider + 1-item kebab (Open ERA).
            Body: 6 stacked fields + chevron expand hint.
            Expanded: ERADetailRow rendered inline beneath the body, verbatim prop bag. */}
        <div className="d-md-none">
          {eras.map((era) => {
            const h = era.header || {};
            const isExpanded = expandedRows.has(era._id);
            const summary = era.accountSummary || { allocated: [], unallocated: [] };
            const allocCount = (summary.allocated || []).reduce(
              (s, a) => s + (a.claimCount || 0),
              0,
            );
            const unallocCount = (summary.unallocated || []).reduce(
              (s, a) => s + (a.claimCount || 0),
              0,
            );

            return (
              <div className="acc-card" key={era._id}>
                <div className="acc-card-top">
                  <span className="acc-card-id">{h.MedKreditERAnbr || "—"}</span>
                  <span className="acc-card-amount">
                    {currency.format(h.TotalPaidProvider || 0)}
                  </span>
                  <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                    <Dropdown.Toggle
                      variant="link"
                      className="acc-card-kebab text-primary text-decoration-none p-0 border-0"
                      aria-label="Row actions"
                    >
                      <i className="fa-solid fa-ellipsis-vertical" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end" className="shadow-sm">
                      <Dropdown.Item
                        onClick={(e) => {
                          e.stopPropagation();
                          handleHeaderClick(era);
                        }}
                      >
                        <i className="fa fa-arrow-up-right-from-square me-2" /> Open ERA
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                <div
                  className="acc-card-body"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleRow(era._id)}
                >
                  <div className="acc-card-field">
                    <span className="acc-card-label">ERA Date</span>
                    <span className="acc-card-value">{h.ERADate || "—"}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Practice</span>
                    <span className="acc-card-value">{h.PracticeNumber || "—"}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Medical Aid</span>
                    <span className="acc-card-value">{h.MedicalAidName || "—"}</span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Paid to Member</span>
                    <span className="acc-card-value acc-list-num">
                      {currency.format(h.TotalPaidMember || 0)}
                    </span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Not Paid</span>
                    <span className="acc-card-value acc-list-num">
                      {currency.format(h.TotalNotPaid || 0)}
                    </span>
                  </div>
                  <div className="acc-card-field">
                    <span className="acc-card-label">Payment</span>
                    <span className="acc-card-value">
                      {h.PaymentPaidDate || "—"} · {h.PaymentReference || "—"}
                    </span>
                  </div>

                  <div className="acc-card-expand-hint">
                    <i
                      className={`fa-solid fa-chevron-${isExpanded ? "down" : "right"} text-primary`}
                    />
                  </div>
                </div>

                {isExpanded && (
                  <div className="acc-card-lines">
                    <ERADetailRow
                      summary={summary}
                      allocCount={allocCount}
                      unallocCount={unallocCount}
                      currency={currency}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}

      {/* Filter Offcanvas */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-3">
            <h6 className="mb-1 text-primary fw-normal ms-3 mb-2">Date range</h6>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>

          <Accordion alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() =>
                      setTmpPractices(practiceList.map((p) => p.practiceNumber))
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  {practiceList.map((practice) => (
                    <Form.Check
                      key={practice._id}
                      type="checkbox"
                      id={`practice-${practice.practiceNumber}`}
                      label={`${practice.practiceNumber} — ${practice.practiceName}`}
                      checked={tmpPractices.includes(practice.practiceNumber)}
                      onChange={() =>
                        setTmpPractices((arr) => toggleIn(arr, practice.practiceNumber))
                      }
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>Medical Aid</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpMedicalAids(medicalAidOptions)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpMedicalAids([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  {medicalAidOptions.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`medaid-${name}`}
                      label={name}
                      checked={tmpMedicalAids.includes(name)}
                      onChange={() =>
                        setTmpMedicalAids((arr) => toggleIn(arr, name))
                      }
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpMedicalAids([]);
                setTmpStartDate("");
                setTmpEndDate("");
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}

/**
 * ERADetailRow — expandable per-ERA detail panel showing every claim on the
 * ERA at transaction level. One row per claim, ordered by treatment date.
 * Allocation status is a column (badge), not a section split — that way the
 * user can scan a single timeline and see which claims landed in which
 * account vs which still need attention.
 */
function ERADetailRow({ summary, allocCount, unallocCount, currency }) {
  // Flatten allocated + unallocated into one transaction-level list, joined
  // back to their owning account (allocated) or medical-aid grouping
  // (unallocated). Sort newest treatment date first.
  const rows = useMemo(() => {
    const out = [];
    for (const acct of summary.allocated || []) {
      for (const c of acct.claims || []) {
        out.push({
          status: "Allocated",
          treatmentDate: c.treatmentDate || "",
          patientName: c.patientName || "",
          accountName: acct.accountName || "(unknown account)",
          accountNumber: acct.accountNumber || "",
          accountMedicalAidNumber: "",
          transactionNumber: c.transactionNumber || "",
          totalClaimed: c.totalClaimed || 0,
          totalPaid: c.totalPaid || 0,
          movedToPatient: c.movedToPatient || 0,
          claimStatus: c.statusAfter || "",
        });
      }
    }
    for (const grp of summary.unallocated || []) {
      for (const c of grp.claims || []) {
        // c.lineTotal is the unallocated entry's paid amount (the new
        // backend builder sums paidAmount). Surface it as both Claimed and
        // Paid so the per-claim Paid column reconciles against the header
        // TotalPaidProvider — money GEMS sent that just hasn't been linked
        // to an account yet is still "paid".
        out.push({
          status: "Unallocated",
          treatmentDate: c.treatmentDate || "",
          patientName: c.patientName || grp.patientName || "(unknown patient)",
          accountName: "",
          accountNumber: "",
          accountMedicalAidNumber: c.accountMedicalAidNumber || grp.accountMedicalAidNumber || "",
          transactionNumber: c.transactionNumber || "",
          totalClaimed: c.lineTotal || 0,
          totalPaid: c.lineTotal || 0,
          movedToPatient: 0,
          claimStatus: "Unallocated",
        });
      }
    }
    return out.sort((a, b) => {
      const ta = a.treatmentDate ? new Date(a.treatmentDate).getTime() : 0;
      const tb = b.treatmentDate ? new Date(b.treatmentDate).getTime() : 0;
      return tb - ta;
    });
  }, [summary]);

  return (
    <div className="p-3 text-start" style={{ background: "#f8fbff" }}>
      <div className="d-flex align-items-center mb-2 gap-3">
        <h6 className="mb-0 text-primary fw-semibold">Claim detail</h6>
        <Badge bg="success" pill>
          <i className="fa-solid fa-circle-check me-1" /> {allocCount} allocated
        </Badge>
        <Badge bg="danger" pill>
          <i className="fa-solid fa-circle-exclamation me-1" /> {unallocCount} unallocated
        </Badge>
      </div>

      {rows.length === 0 ? (
        <div className="text-muted small fst-italic">No claim details for this ERA.</div>
      ) : (
        <Table size="sm" className="mb-0 small" hover bordered>
          <thead>
            <tr>
              <th className="text-primary fw-semibold">Treatment Date</th>
              <th className="text-primary fw-semibold text-center">Status</th>
              <th className="text-primary fw-semibold">Account / Med Aid no.</th>
              <th className="text-primary fw-semibold">Patient</th>
              <th className="text-primary fw-semibold">Transaction No.</th>
              <th className="text-primary fw-semibold text-end" style={{ width: "12%" }}>Claimed</th>
              <th className="text-primary fw-semibold text-end" style={{ width: "12%" }}>Paid</th>
              <th className="text-primary fw-semibold text-end" style={{ width: "12%" }}>To Patient</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const isAllocated = r.status === "Allocated";
              // Sub-statuses for allocated rows — comes from the backend's
              // dateGroup outcome mapping. These distinguish cash that landed
              // somewhere from cash that was already counted in a prior ERA,
              // a duplicate in this ERA, or a negative-amount reversal.
              const isAlreadySettled = r.claimStatus === "Already Settled";
              const isDuplicateInBundle = r.claimStatus === "Duplicate in bundle";
              const isReversal = r.claimStatus === "Reversal / Clawback";
              return (
                <tr key={`${r.transactionNumber || "t"}-${i}`}>
                  <td>{r.treatmentDate || "—"}</td>
                  <td className="text-center align-middle">
                    {!isAllocated ? (
                      <Badge bg="danger" pill>
                        <i className="fa-solid fa-circle-exclamation me-1" /> Unallocated
                      </Badge>
                    ) : isReversal ? (
                      <Badge bg="danger" pill>
                        <i className="fa-solid fa-rotate-left me-1" /> Reversal
                      </Badge>
                    ) : isAlreadySettled ? (
                      <Badge bg="secondary" pill>
                        <i className="fa-solid fa-circle-info me-1" /> Already Settled
                      </Badge>
                    ) : isDuplicateInBundle ? (
                      <Badge bg="warning" text="dark" pill>
                        <i className="fa-solid fa-clone me-1" /> Duplicate
                      </Badge>
                    ) : (
                      <Badge bg="success" pill>
                        <i className="fa-solid fa-circle-check me-1" /> Allocated
                      </Badge>
                    )}
                  </td>
                  <td>
                    {isAllocated ? (
                      <>
                        <div className="fw-semibold">{r.accountName}</div>
                        {r.accountNumber && (
                          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                            {r.accountNumber}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-muted">
                        {r.accountMedicalAidNumber
                          ? `MA #: ${r.accountMedicalAidNumber}`
                          : "(no medical aid number)"}
                      </div>
                    )}
                  </td>
                  <td>{r.patientName || "—"}</td>
                  <td className="text-muted" style={{ fontSize: "0.75rem" }}>
                    {r.transactionNumber || "—"}
                  </td>
                  <td className="text-end align-middle acc-list-num">
                    {currency.format(r.totalClaimed || 0)}
                  </td>
                  <td className="text-end align-middle acc-list-num">
                    {/* Show Paid for every "money landed" row so the column
                        sums to the header TotalPaidProvider. Unallocated rows
                        also now show their paid amount (was "—" before, which
                        is what made the per-claim breakdown look smaller than
                        the header total). */}
                    {currency.format(r.totalPaid || 0)}
                  </td>
                  <td className="text-end align-middle acc-list-num">
                    {isAllocated && r.movedToPatient
                      ? currency.format(r.movedToPatient)
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}
